import { unvalidatedPending, type AssetDetail, type PipelineSummary, type VersionDetail } from '@lastrouter/contracts';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, Field, Textarea } from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { publishState } from './logic.js';
import { ApprovalsCard, ASSET_QUERIES } from './versions.js';

/*
 * Aba Publicar do app: as aprovações dos donos, a publicação normal e o atalho do admin, publicar sem a validação
 * do cliente (ADR 0050), com a lista do que está pendente e justificativa obrigatória.
 */

const MIN_REASON = 10;
const PUBLISH_QUERIES = [...ASSET_QUERIES, 'catalog', 'publications', 'overview'] as const;

function UnvalidatedDialog({ draft, pending, onClose }: { draft: VersionDetail; pending: string[]; onClose: () => void }) {
  const { t } = useTranslation();
  const go = useApiMutation('publishVersionUnvalidated', [...PUBLISH_QUERIES]);
  const [reason, setReason] = useState('');
  const k = 'assets.unvalidated';

  function submit(e: FormEvent) {
    e.preventDefault();
    go.mutate(
      { params: { id: draft.version.id }, body: { justification: reason.trim() } },
      {
        onSuccess: (r) => {
          toast.success(t(`${k}.done`, { n: r.version.number }));
          onClose();
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t(`${k}.dialogTitle`, { n: draft.version.number })} description={t(`${k}.dialogHelp`)} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Alert tone="warning" title={t(`${k}.pendingTitle`, { count: pending.length })}>
            <ul className="list-disc pl-5">
              {pending.map((p) => (
                <li key={p} className="break-words first-letter:uppercase">
                  {p}
                </li>
              ))}
            </ul>
          </Alert>
          <Field id="publicar-sem-validacao-motivo" label={t(`${k}.reason`)} hint={t(`${k}.reasonHint`)}>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} required minLength={MIN_REASON} />
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="danger" disabled={go.isPending || reason.trim().length < MIN_REASON}>
              {t(`${k}.confirm`)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** `certification`: a da última execução do pipeline que terminou bem; undefined sem execução do pipeline (a mesma regra do serviço). */
export function PublishPanel({ detail, dirty, certification }: { detail: AssetDetail; dirty: boolean; certification: PipelineSummary['certification'] | undefined }) {
  const { t } = useTranslation();
  const isAdmin = Boolean(useSession().data?.is_admin);
  const publish = useApiMutation('publishVersion', [...PUBLISH_QUERIES]);
  const [confirming, setConfirming] = useState(false);
  const { draft, published } = detail;
  const pub = publishState(draft, dirty);
  const k = 'assets.unvalidated';

  if (!draft) {
    // A marca da versão publicada sem validação já aparece no topo da página do app.
    return <Alert tone="info" title={published ? t(`${k}.nothingToPublish`, { n: published.number }) : t('assets.nothing')} />;
  }

  const pending = unvalidatedPending(draft.status.pending, certification);
  const refusedBy = draft.version.approvals.filter((a) => a.decision === 'recusado' && !a.no_lugar_de).map((a) => a.by);
  const blocked = refusedBy.length > 0 || draft.issues.length > 0 || dirty || pending.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <ApprovalsCard draft={draft} appName={detail.asset.name} />
      {draft.issues.length ? (
        <Alert tone="warning" title={t('assets.issues')}>
          <ul className="list-disc pl-5">
            {draft.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {dirty ? <Alert tone="warning" title={t('assets.saveFirst')} /> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t(`${k}.normalTitle`)}</CardTitle>
            <CardDescription>{t(`${k}.normalHelp`)}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {draft.status.pending.length ? (
              <ul className="list-disc pl-5 text-[length:var(--lr-text-sm)] text-fg-secondary">
                {draft.status.pending.map((p) => (
                  <li key={p.papel}>{t(`${k}.ownerPending.${p.papel}`, { owner: p.owner })}</li>
                ))}
              </ul>
            ) : null}
            {refusedBy.length ? <Alert tone="danger" title={t('context.editor.refusedState')} /> : null}
            {isAdmin ? (
              <div>
                <Button
                  variant="primary"
                  disabled={pub !== 'pronto' || publish.isPending}
                  onClick={() =>
                    publish.mutate(
                      { params: { id: draft.version.id } },
                      { onSuccess: (r) => toast.success(t('assets.publishedOk', { n: r.version.number })), onError: (err) => toast.error(errorMessage(t, err)) },
                    )
                  }
                >
                  {t('assets.publish')}
                </Button>
              </div>
            ) : (
              <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.adminOnly`)}</p>
            )}
          </CardContent>
        </Card>
        {isAdmin ? (
          <Card>
            <CardHeader>
              <CardTitle>{t(`${k}.title`)}</CardTitle>
              <CardDescription>{t(`${k}.help`)}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {pending.length ? (
                <>
                  <p className="text-[length:var(--lr-text-sm)] font-medium">{t(`${k}.pendingTitle`, { count: pending.length })}</p>
                  <ul className="list-disc pl-5 text-[length:var(--lr-text-sm)] text-fg-secondary">
                    {pending.map((p) => (
                      <li key={p} className="break-words first-letter:uppercase">
                        {p}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.nothingPending`)}</p>
              )}
              {refusedBy.length ? <Alert tone="danger" title={t(`${k}.refused`, { who: refusedBy.join(', ') })} /> : null}
              <div>
                <Button variant="danger" disabled={blocked} onClick={() => setConfirming(true)}>
                  {t(`${k}.open`)}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
      {confirming ? <UnvalidatedDialog draft={draft} pending={pending} onClose={() => setConfirming(false)} /> : null}
    </div>
  );
}
